import { useState } from 'react';
import { ArrowRight, Phone, Mail, MapPin, Building2, Plane, Users, Send } from 'lucide-react';
import { trackContactFormSubmit } from '../utils/analytics';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    operationType: '',
    fleetSize: '',
    message: '',
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const subject = `Gate 33 — Contact from ${formData.name}${formData.company ? ` (${formData.company})` : ''}`;
    const body = `Name: ${formData.name}
Company: ${formData.company || 'N/A'}
Type of Operation: ${formData.operationType || 'N/A'}
Fleet Size: ${formData.fleetSize || 'N/A'}

Message:
${formData.message}`;
    
    window.location.href = `mailto:nicolas@gate33.co?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    trackContactFormSubmit();
  };

  const operationTypes = ['Charter', 'Corporate', 'Management', 'Other'];

  return (
    <div data-testid="contact-page" className="min-h-screen">
      {/* Hero */}
      <section data-testid="contact-hero" className="section-padding bg-[#FDFBF7]">
        <div className="container-custom">
          <div className="max-w-3xl">
            <span className="overline inline-block mb-4">Contact</span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-tight mb-6">
              Let's Discuss Your Operations
            </h1>
            <p className="text-lg text-[#5A5A5A] leading-relaxed">
              Whether you're looking to elevate your cabin standards, structure your service operations, 
              or train your crew — we're here to help.
            </p>
          </div>
        </div>
      </section>

      {/* Form + Info */}
      <section data-testid="contact-form-section" className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
            {/* Form */}
            <div className="lg:col-span-7">
              <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-xs uppercase tracking-[0.15em] font-semibold text-[#1A1A1A] mb-2">
                    Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    data-testid="contact-name"
                    className="w-full px-4 py-3 bg-[#FDFBF7] border border-[#E5E0D8] text-[#1A1A1A] focus:border-[#C5A880] focus:outline-none transition-colors"
                    placeholder="Your full name"
                  />
                </div>

                <div>
                  <label htmlFor="company" className="block text-xs uppercase tracking-[0.15em] font-semibold text-[#1A1A1A] mb-2">
                    Company
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    data-testid="contact-company"
                    className="w-full px-4 py-3 bg-[#FDFBF7] border border-[#E5E0D8] text-[#1A1A1A] focus:border-[#C5A880] focus:outline-none transition-colors"
                    placeholder="Company or operator name"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="operationType" className="block text-xs uppercase tracking-[0.15em] font-semibold text-[#1A1A1A] mb-2">
                      Type of Operation
                    </label>
                    <select
                      id="operationType"
                      name="operationType"
                      value={formData.operationType}
                      onChange={handleChange}
                      data-testid="contact-operation-type"
                      className="w-full px-4 py-3 bg-[#FDFBF7] border border-[#E5E0D8] text-[#1A1A1A] focus:border-[#C5A880] focus:outline-none transition-colors appearance-none"
                    >
                      <option value="">Select type</option>
                      {operationTypes.map((type) => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label htmlFor="fleetSize" className="block text-xs uppercase tracking-[0.15em] font-semibold text-[#1A1A1A] mb-2">
                      Fleet Size
                    </label>
                    <input
                      type="text"
                      id="fleetSize"
                      name="fleetSize"
                      value={formData.fleetSize}
                      onChange={handleChange}
                      data-testid="contact-fleet-size"
                      className="w-full px-4 py-3 bg-[#FDFBF7] border border-[#E5E0D8] text-[#1A1A1A] focus:border-[#C5A880] focus:outline-none transition-colors"
                      placeholder="Number of aircraft"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="message" className="block text-xs uppercase tracking-[0.15em] font-semibold text-[#1A1A1A] mb-2">
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={6}
                    value={formData.message}
                    onChange={handleChange}
                    data-testid="contact-message"
                    className="w-full px-4 py-3 bg-[#FDFBF7] border border-[#E5E0D8] text-[#1A1A1A] focus:border-[#C5A880] focus:outline-none transition-colors resize-none"
                    placeholder="Tell us about your operation and how we can help..."
                  />
                </div>

                <button
                  type="submit"
                  data-testid="contact-submit"
                  className="btn-primary inline-flex items-center gap-2"
                >
                  Send Message
                  <Send size={16} />
                </button>
              </form>
            </div>

            {/* Contact Info */}
            <div className="lg:col-span-5">
              <div className="bg-[#1A1A1A] p-8 lg:p-10 h-full">
                <h3 className="font-heading text-2xl text-[#FDFBF7] mb-8">
                  Direct Contact
                </h3>

                <div className="space-y-6 mb-12">
                  <a href="mailto:nicolas@gate33.co" className="flex items-start gap-4 group">
                    <Mail size={18} className="text-[#C5A880] mt-0.5" />
                    <div>
                      <p className="text-xs uppercase tracking-wider text-[#C5A880] mb-1">Email</p>
                      <p className="text-[#FDFBF7] group-hover:text-[#C5A880] transition-colors">nicolas@gate33.co</p>
                    </div>
                  </a>

                  <div className="flex items-start gap-4">
                    <MapPin size={18} className="text-[#C5A880] mt-0.5" />
                    <div>
                      <p className="text-xs uppercase tracking-wider text-[#C5A880] mb-1">Base</p>
                      <p className="text-[#9A9A9A]">Gate 33 is LFMN based</p>
                    </div>
                  </div>
                </div>

                <div className="border-t border-[#2A2A2A] pt-8">
                  <h4 className="text-xs uppercase tracking-wider text-[#C5A880] mb-4">We Help With</h4>
                  <ul className="space-y-3">
                    <li className="flex items-center gap-3 text-[#9A9A9A] text-sm">
                      <Building2 size={14} className="text-[#C5A880]" />
                      Cabin operations & standards
                    </li>
                    <li className="flex items-center gap-3 text-[#9A9A9A] text-sm">
                      <Users size={14} className="text-[#C5A880]" />
                      Crew training & recruitment
                    </li>
                    <li className="flex items-center gap-3 text-[#9A9A9A] text-sm">
                      <Plane size={14} className="text-[#C5A880]" />
                      Catering & service excellence
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
